import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import carticon from "../Assets/Indorinama-website-elements/Icons/carticondark.svg";
import { useAuth } from "../authContext";
import { useCart } from "../cartContext";
import RegisterModal from "../Modals/RegisterModal";
import { GiHamburgerMenu } from "react-icons/gi";
import { IoClose } from "react-icons/io5";
import { FiBox, FiUser, FiLogOut } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";

const navLinks = [
  {
    name: "Advertising",
    link: "/advertising",
  },
  {
    name: "Designing",
    link: "/designing",
  },
  {
    name: "Digital Marketing",
    link: "/digital-marketing",
  },
  // {
  //   name: "Brand Endorsement",
  //   link: "/brand-endorsement",
  // },
  {
    name: "Printing",
    link: "/printing",
  },
  // {
  //   name: "Influencer Marketing",
  //   link: "/influencer-marketing",
  // },
];

const UserPopover = ({ isOpen, onClose, buttonRef, handleOrders, handleMyInfo, handleLogout }) => {
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const popoverRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + 12,
          right: document.documentElement.clientWidth - rect.right,
        });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, buttonRef]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="fixed z-[9999999] w-56 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden"
          style={{
            top: `${coords.top}px`,
            right: `${coords.right}px`,
          }}
        >
          <div className="py-2">
            <button
              onClick={handleOrders}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#bc1535] transition-colors"
            >
              <FiBox className="text-lg" />
              <span>My Orders</span>
            </button>
            <button
              onClick={handleMyInfo}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#bc1535] transition-colors"
            >
              <FiUser className="text-lg" />
              <span>My Profile</span>
            </button>
            <div className="h-px bg-gray-100 my-1 mx-3" />
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#bc1535] transition-colors"
            >
              <FiLogOut className="text-lg" />
              <span>Log Out</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const Navbar = ({ subMenu = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, logout } = useAuth();
  const { cartCount } = useCart();
  const [modal, setModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const userButtonRef = useRef(null);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setPopoverOpen(false);
  };

  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setModal(true);
    setIsMenuOpen(false);
  };

  const handleOrders = () => {
    navigate("/my-orders");
    setPopoverOpen(false);
  };

  const handleMyInfo = () => {
    navigate("/myinfo");
    setPopoverOpen(false);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavLinkClick = (link) => {
    setIsMenuOpen(false);

    if (location.pathname === link) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header className="sticky top-0 left-0 right-0 bg-black shadow-sm z-50 w-full">
      {/* Header Section */}
      <div className="container mx-auto px-4 overflow-hidden">
        <div className="flex items-center justify-between h-20">
          {/* Left Logo */}
          <div className="flex-shrink-0 hidden md:block">
            <NavLink to="/">
              <img src="/in-logo-full.svg" alt="Indorinama Logo" className="h-[4.5rem]" />
            </NavLink>
          </div>

          {/* Center Logo */}
          <div className="flex-shrink-0">
            <NavLink to="/">
              <img src="/in_logo.jpg" alt="Indorinama Icon" className="h-[5.4rem]" />
            </NavLink>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Cart Icon */}
            <NavLink
              to={isLoggedIn ? "/cart" : "#"}
              onClick={() => !isLoggedIn && openAuthModal("login")}
              className="relative"
            >
              <img src={carticon} alt="Cart" className="h-8 w-8" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                  {cartCount}
                </span>
              )}
            </NavLink>

            {/* User Menu */}
            {isLoggedIn ? (
              <div className="relative">
                <button
                  ref={userButtonRef}
                  onClick={() => setPopoverOpen(!isPopoverOpen)}
                  className="h-8 w-8 rounded-full bg-black text-white flex items-center justify-center transition-transform hover:scale-105 hover:bg-gray-800"
                >
                  <i className="fa-solid fa-user text-sm"></i>
                </button>

                <UserPopover
                  isOpen={isPopoverOpen}
                  onClose={() => setPopoverOpen(false)}
                  buttonRef={userButtonRef}
                  handleOrders={handleOrders}
                  handleMyInfo={handleMyInfo}
                  handleLogout={handleLogout}
                />
              </div>
            ) : (
              <div className="hidden items-center gap-2 sm:flex">
                <button
                  onClick={() => openAuthModal("login")}
                  className="rounded-md bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-black transition-colors hover:bg-gray-200 sm:px-4"
                >
                  Login
                </button>
                <button
                  onClick={() => openAuthModal("register")}
                  className="rounded-md border border-white bg-black px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-white hover:text-black sm:px-4"
                >
                  Create Account
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={handleMenuToggle}
              className="rounded-md border border-white/10 p-2 text-white transition-colors hover:border-white/30 md:hidden"
            >
              {isMenuOpen ? (
                <IoClose size={24} />
              ) : (
                <GiHamburgerMenu size={24} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Section */}
      {subMenu && (
        <nav className="h-auto bg-brand-red">
          <div className="container mx-auto px-0 md:px-4">
            <div className="overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <ul className="mb-0 flex min-w-max items-center justify-start gap-4 py-2.5 md:min-w-0 md:justify-center md:gap-4 md:py-3">
              {navLinks.map((item) => {
                return (
                  <li key={item.link}>
                    <NavLink
                      to={item.link}
                      onClick={() => handleNavLinkClick(item.link)}
                      className={({ isActive }) =>
                        `flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 no-underline md:px-4 md:text-base
                      ${isActive
                          ? "bg-white text-black shadow-lg hover:bg-black hover:text-white"
                          : "bg-brand-black text-white hover:bg-white hover:[color:black!important]"}`
                      }
                    >
                      <span>{item.name}</span>
                    </NavLink>
                  </li>
                );
              })}
              </ul>
            </div>
          </div>
        </nav>
      )}


      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 md:hidden transition-opacity ${isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={handleMenuToggle}
      >
        <div
          className={`fixed inset-y-0 left-0 w-[80%] bg-black transform transition-transform ${isMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4">
            <div className="flex justify-between items-center mb-8">
              <NavLink to="/" onClick={() => setIsMenuOpen(false)}>
                <img src="/in_logo.jpg" alt="Indorinama Icon" className="h-12" />
              </NavLink>
              <button
                onClick={handleMenuToggle}
                className="text-white hover:text-gray-900"
              >
                <IoClose size={24} />
              </button>
            </div>

            <ul className="space-y-4 m-0 p-0">
              {navLinks.map((item) => {
                return (
                  <li key={item.link}>
                    <NavLink
                      to={item.link}
                      onClick={() => handleNavLinkClick(item.link)}
                      className={({ isActive }) =>
                        `w-full flex items-center gap-2 px-4 py-2 rounded-lg text-lg transition-colors no-underline ${isActive
                          ? "bg-[#bc1535] text-white font-medium"
                          : "text-white"
                        }`
                      }
                    >
                      <span>{item.name}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>

            {!isLoggedIn && (
              <div className="mt-8 flex flex-col gap-3 border-t border-white/10 pt-6">
                <button
                  onClick={() => openAuthModal("login")}
                  className="w-full rounded-md bg-white px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-black transition-colors hover:bg-gray-200"
                >
                  Login
                </button>
                <button
                  onClick={() => openAuthModal("register")}
                  className="w-full rounded-md border border-white bg-black px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-white hover:text-black"
                >
                  Create Account
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <RegisterModal
        isOpen={modal}
        toggle={() => setModal(!modal)}
        initialMode={authMode}
      />
    </header>
  );
};

export default Navbar;
